import { AGE_BAND_OPTIONS, labelFor, procedureLabel } from '@iculogbook/shared';
import { useState } from 'react';

import { Button, Sheet } from '../../components/index.ts';
import { ProcedureFields } from './ProcedureForm.tsx';
import type { FormErrors } from './ProcedureForm.tsx';
import { ProcedurePicker } from './ProcedurePicker.tsx';
import { applyProcedureChange } from './procedureForm.ts';
import type { ProcedureFormValues } from './procedureForm.ts';

type NestedProcedureSheetProps = {
  open: boolean;
  /**
   * The procedure being edited, or null to start from the picker. Held by the
   * case form, so closing the sheet does not lose what was typed.
   */
  values: ProcedureFormValues | null;
  onSelectProcedure: (procedureKey: string) => void;
  onChange: (values: ProcedureFormValues) => void;
  onDone: () => void;
  onRemove: () => void;
  onClose: () => void;
  recentKeys: readonly string[];
  errors: FormErrors;
};

/**
 * Adding or editing a procedure inside a case, in a bottom sheet over the case
 * form so the case stays visible behind it.
 *
 * It opens on the picker for a new procedure and on the fields for an existing
 * one. The fields are the same component the standalone form uses — a procedure
 * inside a case is a procedure, and the moment the two forms diverge is the
 * moment one of them starts collecting something the other cannot.
 */
export function NestedProcedureSheet({
  open,
  values,
  onSelectProcedure,
  onChange,
  onDone,
  onRemove,
  onClose,
  recentKeys,
  errors,
}: NestedProcedureSheetProps) {
  const [picking, setPicking] = useState(false);
  const choosing = values === null || picking;

  function selectProcedure(procedureKey: string): void {
    if (values === null) {
      onSelectProcedure(procedureKey);
    } else {
      onChange(applyProcedureChange(values, procedureKey));
    }
    setPicking(false);
  }

  return (
    <Sheet
      open={open}
      onClose={() => {
        setPicking(false);
        onClose();
      }}
      tone="nested"
      title={
        choosing ? 'Add a procedure' : `${procedureLabel(values.procedureKey)} — inside this case`
      }
      footer={
        choosing ? undefined : (
          <div className="flex gap-2">
            <Button variant="danger" onClick={onRemove}>
              Remove
            </Button>
            <Button variant="primary" block onClick={onDone}>
              Done
            </Button>
          </div>
        )
      }
    >
      {choosing ? (
        <ProcedurePicker
          onSelect={selectProcedure}
          recentKeys={recentKeys}
          selectedKey={values?.procedureKey}
          autoFocus
        />
      ) : (
        <div className="flex flex-col gap-4">
          <InheritedSummary values={values} />
          <Button variant="secondary" onClick={() => setPicking(true)}>
            Change procedure
          </Button>
          <ProcedureFields values={values} onChange={onChange} errors={errors} />
        </div>
      )}
    </Sheet>
  );
}

const WHEN = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * States plainly what this procedure took from the case, rather than leaving
 * the trainee to notice that three fields happen to be pre-filled.
 *
 * The copying itself is not new — a nested procedure has always inherited the
 * case's date, age band and capability level, and follows later changes to the
 * case until it is overridden here. What was missing was saying so.
 */
function InheritedSummary({ values }: { values: ProcedureFormValues }) {
  const copied = [
    WHEN.format(values.occurredAt),
    values.ageBand === null ? null : labelFor(AGE_BAND_OPTIONS, values.ageBand),
    values.capabilityLevel === null ? null : `Level ${String(values.capabilityLevel)}`,
  ].filter((part): part is string => part !== null);

  const missing = values.ageBand === null || values.capabilityLevel === null;

  return (
    <div className="rounded-md border border-accent/30 bg-accent-quiet px-3 py-2">
      <p className="text-xs text-muted">
        <span className="font-medium text-text">Copied from the case:</span> {copied.join(' · ')}
      </p>
      <p className="pt-1 text-xs text-faint">
        {missing
          ? 'Fill in the case’s age band and capability level and they will be copied here too. Anything you change below belongs to this procedure alone.'
          : 'Change any of them below and this procedure keeps its own.'}
      </p>
    </div>
  );
}
